// @flow
import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {Typography, Box, Divider} from '@material-ui/core';
import {useForm} from 'react-hook-form';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import {colors} from '../../../../assets/styles/colors';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';

import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';

import type {SubcategoryRequest} from '../../../sub-categories/duck';
import type {Category} from '../../../../entities';

type Props = {
  open: boolean,
  handleClose: () => void,
  categories?: Category[],
  subcategory?: {[key: string]: any},
  editSubcategory?: ({subcategoryRequest: SubcategoryRequest}) => void,
  updateSubcategorySuccess?: boolean,
  createSubcategory: ({subcategoryRequest: SubcategoryRequest}) => void,
  createSubcategorySuccess?: boolean,
  isSaving?: boolean,
  onSubmit?: () => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    textAlign: 'center',
    // height: 240,
    padding: theme.spacing(4, 0),
  },
  content: {
    maxWidth: 500,
    width: '100%',
    margin: 'auto',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
  },
  subtitle: {
    textAlign: 'start',
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  confirmButton: {
    marginTop: theme.spacing(3),
    width: '100%',
    fontWeight: 600,
    fontSize: '18px',
  },
  selectText: {
    color: '#6A6A7F',
    fontSize: '16px',
    fontWeight: 500,
    textAlign: 'left',
  },
  selectCategory: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  bigSelect: {
    paddingTop: '30px',
  },
}));

export default function SubcategoryModal(props: Props) {
  const {
    open,
    handleClose,
    categories,
    subcategory,
    isSaving,
    createSubcategory,
    editSubcategory,
    createSubcategorySuccess,
    onSubmit,
  } = props;
  const classes = useStyles();

  const {register, handleSubmit, errors, reset} = useForm();

  const handleSubcategorySubmit = data => {
    if (!subcategory) {
      createSubcategory({subcategoryRequest: data});
    } else if (subcategory && subcategory.subcategory) {
      if (subcategory.subcategory.name !== data.name && editSubcategory) {
        editSubcategory({
          id: subcategory.subcategory.id,
          subcategoryRequest: data,
        });
      }
    }

    handleClose();
    onSubmit && onSubmit();
  };

  useEffect(() => {
    if (createSubcategorySuccess) {
      reset();
    }
  }, [createSubcategorySuccess, reset]);

  const defaultCategory = categories && categories[0] ? categories[0].id : '';

  return (
    <CustomModal open={open} handleCloseModal={handleClose}>
      <div className={classes.root}>
        <Box className={classes.content}>
          <Typography align="center" gutterBottom className={classes.title}>
            {subcategory ? 'Editar subcategoría' : 'Nueva subcategoría'}
          </Typography>
          <Typography align="center" className={classes.subtitle}>
            Para mejorar el diseño de tu tienda, asegúrate de encuadrar los dos
            estilos: cuadrado y rectangular.
          </Typography>
          <Divider className={classes.divider} />

          <div className={classes.selectCategory}>
            <Typography className={classes.selectText}>
              Selecciona la categoría a la que pertenece
            </Typography>
            <CustomSelect
              classes={{root: classes.bigSelect}}
              native
              fullWidth
              label="Seleccionar categoría"
              defaultValue={
                subcategory && subcategory.category
                  ? subcategory.category.id
                  : defaultCategory
              }
              name="category"
              error={!!errors.category}
              helperText={errors.category ? errors.category.message : ''}
              inputRef={register({
                required: 'Campo obligatorio',
              })}
              disabled={isSaving || !!(subcategory && subcategory.subcategory)}
            >
              <option value="" hidden />
              {categories &&
                categories.map((opt, idx) => (
                  <option key={`not-renew-option-${idx + 1}`} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
            </CustomSelect>
            <CustomTextField
              name="name"
              placeholder="Nombre de la subcategoría"
              defaultValue={
                subcategory && subcategory.subcategory
                  ? subcategory.subcategory.name
                  : ''
              }
              error={!!errors.name}
              helperText={errors.name ? errors.name.message : ''}
              inputRef={register({
                required: 'Campo obligatorio',
                pattern: {
                  value: /^\S.*$/,
                  message: 'Formato incorrecto',
                },
              })}
              disabled={isSaving}
            />
          </div>
          <CustomButton
            variant="contained"
            fullWidth
            disabled={isSaving}
            className={classes.confirmButton}
            onClick={handleSubmit(handleSubcategorySubmit)}
          >
            {subcategory ? 'Editar subcategoría' : 'Agregar subcategoría'}
          </CustomButton>
        </Box>
      </div>
    </CustomModal>
  );
}

SubcategoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  subcategory: PropTypes.object,
  categories: PropTypes.array,
  editSubcategory: PropTypes.func.isRequired,
  updateSubcategorySuccess: PropTypes.bool,
  createSubcategory: PropTypes.func.isRequired,
  createSubcategorySuccess: PropTypes.bool,
  isSaving: PropTypes.bool,
};
